// The signed-in user's role ON a company, resolved from /api/me and cached per company in
// sessionStorage. Shared by the top-bar Admin/Member badge (tshell.js), the document editor
// (Finalise vs "Submit for approval"), and the approvals screen (admin queue vs a member's own
// submissions). `is_admin` mirrors identity.role.can_manage_users — the exact flag the API's
// confirm-route gate and document_approvals_service._is_company_admin use — so the UI and the
// server always agree on who may finalise. Owner 2026-09-10.
import { signal } from './t4.js';
import { api } from './api.js';

const K_ROLE = 'sleek_co_role_';
// Fallback classification for BEFORE the API's is_admin flag is deployed (or an older cached row):
// the two seeded roles that carry can_manage_users. Once /api/me sends is_admin this is unused.
const ADMIN_ROLE_CODES = new Set(['owner', 'accountant', 'admin']);

// signal({ code, name, is_admin }) | signal(null). Cached per company id in sessionStorage.
// ALWAYS revalidates against /api/me in the background: the cached value paints instantly, but a
// role that changed server-side (owner/admin → user, or an older pre-is_admin cache) must not leave
// the badge or the Finalise/Submit gate showing the OLD role. Without this, a demoted user kept
// seeing "Admin" and admin controls for the rest of the session even though the server (correctly)
// treated them as a normal user — the "I'm shown admin but behave as a user" bug (owner 2026-09-11).
// The signal is only ever REPLACED with a role /me actually returns for this company; if /me doesn't
// include the company (transient, or the caller has none) the cached value is kept — never cleared
// here, so an edge response can't wrongly demote the badge.
export function companyRole(id){
  const read = () => { try { return JSON.parse(sessionStorage.getItem(K_ROLE + id) || 'null'); } catch { return null; } };
  const role = signal(read());
  (async () => {
    try {
      const me = await api.get('/me');
      const co = ((me && me.companies) || []).find(c => String(c.id) === String(id));
      if(co){
        const r = { code: co.role_code || '', name: co.role_name || co.role_code || '',
                    is_admin: typeof co.is_admin === 'boolean' ? co.is_admin : ADMIN_ROLE_CODES.has((co.role_code || '').toLowerCase()) };
        if(JSON.stringify(r) !== JSON.stringify(role.value)) role.value = r;   // only repaint on a real change
        try { sessionStorage.setItem(K_ROLE + id, JSON.stringify(r)); } catch {}
      }
    } catch { /* offline / transient — keep the cached value */ }
  })();
  return role;
}

// True when the resolved role carries admin authority (may finalise directly). Falls back to the
// role code while is_admin is absent, so it is correct across the API/web deploy ordering.
export function isCompanyAdmin(roleVal){
  if(!roleVal) return false;
  if(typeof roleVal.is_admin === 'boolean') return roleVal.is_admin;
  return ADMIN_ROLE_CODES.has((roleVal.code || '').toLowerCase());
}

// The three visible access levels (owner directive 2026-09-11): 'owner' | 'admin' | 'user'. The
// company owner is shown as Owner (not "Admin" — that conflated the owner with a platform admin,
// issue #60); any other can_manage_users role is an Admin; everyone else is a User.
export function accessLevel(roleVal){
  if(!roleVal) return 'user';
  if((roleVal.code || '').toLowerCase() === 'owner') return 'owner';
  return isCompanyAdmin(roleVal) ? 'admin' : 'user';
}
